import Footer from '@components/organisms/FooterNew'
import HeroSection from '@components/pages/about/HeroSection'
import InfoSection from '@components/pages/about/InfoSection'
import VisionSection from '@components/pages/about/VisionSection'
import Explore from '@components/pages/advantageous/Explore'
import FAQSection from '@components/pages/advantageous/FAQSection'
import Header from '@components/pages/home/LandingPage/Sections/Header'
import TrustedStudents from '@components/pages/home/LandingPage/Sections/TrustedStudents'
import { useAuth } from '@contexts/Auth'
import { Spacer } from '@nextui-org/react-latest'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AuthorDetailsModal from '@components/pages/become-author/AuthorDetailsModal'
import withLenis from '@components/HOCs/LenisWrapper'

type Props = {}

const AboutUs = ({}: Props) => {
  const [showBecomeAuthorModal, setShowBecomeAuthorModal] = useState(false)
  const { user, isAuthenticated, setShowAuthModal } = useAuth()
  const freshLoggedIn = useRef(false)
  const router = useRouter()
  const { i18n, t } = useTranslation('about')

  useEffect(() => {
    if (user && !user?.isAuthor && freshLoggedIn.current) {
      setShowBecomeAuthorModal(true)
    }

    return () => {
      freshLoggedIn.current = false
    }
  }, [user])

  const handleBecomeCreator = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true)
      freshLoggedIn.current = true
      return
    }
    if (user?.isAuthor) router.push('/author')
    else setShowBecomeAuthorModal(true)
  }

  return (
    <div>
      <Head>
        <title>{t('meta.title')}</title>
        <meta name="og:title" content={t('meta.ogTitle')} />
        <meta name="description" content={t('meta.description')} />
        <meta name="og:description" content={t('meta.ogDescription')} />
        <link
          rel="canonical"
          href={`https://www.edugram.io/resources/about-us/`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={'Edugram.io'} />
        <meta name="twitter:description" content={t('meta.description')} />
        <meta
          property="og:image"
          content="/static/images/about/og-image.png.png"
        />
        <meta
          property="og:image:secure_url"
          content="/static/images/about/og-image.png"
        />
        <meta property="og:type" content="online_course" />
        <meta property="og:site_name" content="Edugram.io" />
        <meta property="og:locale" content={i18n.language} />
        <meta name="twitter:domain" content="www.edugram.io" />
        <meta name="keywords" content={t('meta.keywords')} />
      </Head>
      <Header handleBecomeCreator={handleBecomeCreator} />
      <HeroSection onJoinNow={handleBecomeCreator} />
      <InfoSection />
      <Spacer y={20} />
      <VisionSection />
      <TrustedStudents handleBecomeCreator={handleBecomeCreator} />
      <FAQSection />
      <Explore />
      {showBecomeAuthorModal && (
        <AuthorDetailsModal
          isOpen={showBecomeAuthorModal}
          onClose={(ask?: boolean) => {
            if (ask) {
              if (confirm('Are you sure you want to close?')) {
                setShowBecomeAuthorModal((prev) => !prev)
              }
            } else {
              setShowBecomeAuthorModal((prev) => !prev)
            }
          }}
        />
      )}
      <div className="max-w-desktop mx-auto sm:px-4">
        <Footer />
      </div>
    </div>
  )
}

export default withLenis(AboutUs)

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'authentication',
        'footer',
        'courses',
        'signUp',
        'home',
        'wishlistDropdown',
        'errors',
        'advantageous',
        'homeLanding/header',
        'homeLanding/safecontent',
        'homeLanding/trustedstudents',
        'becomeAuthorDetailsModal',
        'resources/migrateCourses',
        'resources/onlineCourses',
        'about',
        'reportProblem',
      ])),
    },
  }
}
